// KyckSocial — how a post's state is SAID to a club. Pure.
//
// ⚠️ A pilot post is a real public post. The club must tell a draft from a
// published post at a glance, so each status has its own plain-English label AND a
// sentence stating whether the public can see it. Nothing is ambiguous, and no two
// statuses share a label.

export type PostStatusView = {
  label: string;
  /** Can members of the public see this on the page right now? */
  public: boolean;
  sentence: string;
  tone: 'neutral' | 'info' | 'success' | 'warning' | 'danger';
};

export function postStatusView(status: string, scheduledFor: string | null): PostStatusView {
  switch (status) {
    case 'draft':
      return { label: 'Draft', public: false, sentence: 'Not published. Only you can see this.', tone: 'neutral' };
    case 'scheduled':
      return {
        label: 'Scheduled',
        public: false,
        sentence: scheduledFor ? `Will publish on ${scheduledFor.slice(0, 10)}. Not public yet.` : 'Will publish later. Not public yet.',
        tone: 'info',
      };
    case 'publishing':
      return { label: 'Publishing…', public: false, sentence: 'Going out now. Check back in a minute.', tone: 'info' };
    case 'published':
      return { label: 'Published', public: true, sentence: 'Live on your page.', tone: 'success' };
    case 'partial':
      return {
        label: 'Partly published',
        public: true,
        sentence: 'Live on some platforms, but at least one did not go out. See the details.',
        tone: 'warning',
      };
    case 'failed':
      return { label: 'Failed', public: false, sentence: 'This did not publish.', tone: 'danger' };
    default:
      return { label: status, public: false, sentence: '', tone: 'neutral' };
  }
}

/** Can the club still edit / delete it? A published post cannot be unpublished from here. */
export function postIsEditable(status: string): boolean {
  return status === 'draft' || status === 'scheduled' || status === 'failed';
}

export const PLATFORM_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  googlebusiness: 'Google Business',
};

/** The sentence the confirm step shows before anything goes public. */
export function confirmSentence(mode: 'schedule' | 'now', clubName: string, platforms: string[], whenLocal: string | null): string {
  const where = platforms.map((p) => PLATFORM_LABELS[p] ?? p).join(', ');
  return mode === 'now'
    ? `This will publish to ${where} for ${clubName} right now. It will be public straight away.`
    : `This will publish to ${where} for ${clubName}${whenLocal ? ` on ${whenLocal}` : ''}. It is not public until then.`;
}
